import { supabaseClient } from '../config/supabase';

// The inbox call is capped at 100 rows by the database. One alert is up to four rows (in-app, push, email, text), so the old 50 held
// about a dozen alerts and a busy burst pushed the oldest unread ones out of the bell and its count.
export const DEFAULT_NOTIFICATION_INBOX_LIMIT = 100;
export const CONVERSATION_ENGAGED_EVENT = 'ia:conversation-engaged';

export function announceConversationEngaged(sessionId) {
    const id = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!id || typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(CONVERSATION_ENGAGED_EVENT, { detail: { sessionId: id } }));
}

export function notificationDeliveryLabel(notification) {
    if (notification.channel === 'push'
        && notification.deliveryStatus === 'failed'
        && notification.errorMessage === 'No enabled push tokens found for the notification recipient.') {
        return 'No device registered';
    }
    return notification.deliveryStatus;
}

function pickString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }
    return '';
}

function humanizeEventType(eventType = '') {
    const label = String(eventType || '')
        .split('.')
        .filter(Boolean)
        .slice(-2)
        .join(' ')
        .replace(/[_-]+/g, ' ')
        .trim();
    return label ? label.replace(/\b\w/g, (char) => char.toUpperCase()) : 'Notification';
}

export function normalizeNotificationInboxRow(row = {}) {
    const payload = row.payload && typeof row.payload === 'object' && !Array.isArray(row.payload)
        ? row.payload
        : {};
    const title = pickString(
        payload.title,
        payload.subject,
        payload.notification?.title,
        payload.default?.title,
        row.workflow_display_name,
        humanizeEventType(row.event_type)
    );
    const body = pickString(
        payload.body,
        payload.message,
        payload.text,
        payload.notification?.body,
        payload.default?.body,
        row.recipient_label
    );

    return {
        id: row.id,
        channel: row.channel || 'in_app',
        eventType: row.event_type || '',
        workflowKey: row.workflow_key || '',
        title,
        body,
        deliveryStatus: row.delivery_status || '',
        errorMessage: row.error_message || '',
        payload,
        createdAt: row.created_at || row.queued_at || null,
        inboxReadAt: row.inbox_read_at || null,
        providerReadAt: row.provider_read_at || null,
        isRead: Boolean(row.inbox_read_at),
    };
}

export async function fetchNotificationInbox({
    client = supabaseClient,
    limit = DEFAULT_NOTIFICATION_INBOX_LIMIT,
} = {}) {
    const { data, error } = await client.rpc('bl176_get_my_notification_inbox', {
        p_limit: limit,
    });
    if (error) throw error;
    const rows = data || [];
    // The inbox RPC does not expose provider errors. Read only its failed push
    // IDs through the existing recipient-scoped notifications RLS.
    const failedPushIds = rows.filter((row) => row.channel === 'push' && row.delivery_status === 'failed').map((row) => row.id);
    let errorsById = {};
    if (failedPushIds.length) {
        const result = await client.from('notifications').select('id, error_message')
            .in('id', failedPushIds).eq('channel', 'push');
        if (result.error) throw result.error;
        errorsById = Object.fromEntries((result.data || []).map((row) => [row.id, row.error_message]));
    }
    return rows.map((row) => normalizeNotificationInboxRow({
        ...row, error_message: errorsById[row.id] ?? row.error_message,
    }));
}

export async function markNotificationRead({
    notificationId,
    client = supabaseClient,
} = {}) {
    if (!notificationId) throw new Error('notificationId is required.');
    const { data, error } = await client.rpc('bl176_mark_notification_read', {
        p_notification_id: notificationId,
    });
    if (error) throw error;
    return Array.isArray(data) ? data[0] : data;
}

export async function markAllNotificationsRead({ client = supabaseClient } = {}) {
    const { data, error } = await client.rpc('bl176_mark_all_notifications_read');
    if (error) throw error;
    return data || 0;
}

// ---------------------------------------------------------------------------------------------
// BL-177 Step 2d (section 6): the bell panel and the in-app pop-up.
// ---------------------------------------------------------------------------------------------

/**
 * One alert is queued once per channel (in-app, push, email, text), so the inbox can hold four rows for one text.
 * The bell shows it once: rows queued together for the same event + conversation collapse into one item (the in-app
 * row speaks for it); reading the item reads every row.
 */
export function collapseInboxRows(rows = []) {
    const groups = new Map();
    for (const row of rows) {
        const sessionId = typeof row.payload?.sessionId === 'string' ? row.payload.sessionId : '';
        const key = row.createdAt ? `${row.eventType}|${sessionId}|${row.createdAt}` : `id|${row.id}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(row);
    }
    const items = [];
    for (const [baseKey, group] of groups.entries()) {
        // BL-177 Step 2o (section 4): rows with the same event, conversation AND timestamp are not always ONE alert. Several texts stored in
        // one database transaction (the every-minute backup stores a batch) share one timestamp, and used to merge into a single bell row,
        // so all but one of them were lost. One alert has at most one row per channel, so when a channel has k rows, there are k alerts:
        // the n-th row of each channel belongs to the n-th alert (rows sorted by id so the pairing is the same on every load).
        const byChannel = new Map();
        const order = new Map(group.map((row, index) => [row, index]));
        for (const row of [...group].sort((a, b) => String(a.id).localeCompare(String(b.id)))) {
            const channel = row.channel || 'in_app';
            if (!byChannel.has(channel)) byChannel.set(channel, []);
            byChannel.get(channel).push(row);
        }
        const alerts = Math.max(...[...byChannel.values()].map((list) => list.length));
        for (let n = 0; n < alerts; n += 1) {
            const rowsOfAlert = [...byChannel.values()].map((list) => list[n]).filter(Boolean).sort((a, b) => order.get(a) - order.get(b));
            items.push(buildCollapsedItem(alerts === 1 ? baseKey : `${baseKey}#${n}`, rowsOfAlert));
        }
    }
    return items;
}

function buildCollapsedItem(key, group) {
    const representative = group.find((row) => row.channel === 'in_app') || group[0];
    return {
        key,
        id: representative.id,
        ids: group.map((row) => row.id),
        eventType: representative.eventType,
        payload: representative.payload || {},
        createdAt: representative.createdAt,
        channel: representative.channel,
        deliveryStatus: representative.deliveryStatus,
        errorMessage: representative.errorMessage,
        title: representative.title,
        body: representative.body,
        hasInApp: group.some((row) => row.channel === 'in_app'),
        isRead: group.every((row) => row.isRead),
    };
}

/** "now", "2m", "11:42 PM" (today), "Yesterday", "Mon" (this week), "Oct 3". */
export function formatBellTime(value, now = new Date()) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const minutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
    if (days === 0) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (days === 1) return 'Yesterday';
    if (days < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const SAFE_THREAD_URL = /^\/communications\?session=([0-9a-f-]{36})$/i;
// Teams PR B: a mirrored Teams conversation; teamsKind says which Communications tab opens it (Team chat or client).
const SAFE_TEAMS_THREAD_URL = /^\/communications\?teamsThread=([0-9a-f-]{36})(?:&teamsKind=(?:team|client))?$/i;

/** The conversation an item opens, when it is one of ours (never another origin). */
export function notificationThreadUrl(item) {
    const url = item?.payload?.threadUrl;
    return typeof url === 'string' && (SAFE_THREAD_URL.test(url) || SAFE_TEAMS_THREAD_URL.test(url)) ? url : '';
}

function notificationSessionId(item) {
    const fromUrl = SAFE_THREAD_URL.exec(notificationThreadUrl(item));
    if (fromUrl) return fromUrl[1].toLowerCase();
    const sessionId = item?.payload?.sessionId;
    return typeof sessionId === 'string' ? sessionId.toLowerCase() : '';
}

/** The conversation the person is reading right now ('' when not on one). */
export function activeThreadIdFromLocation(location = typeof window !== 'undefined' ? window.location : null) {
    if (!location || !String(location.pathname || '').startsWith('/communications')) return '';
    try {
        return (new URLSearchParams(location.search).get('session') || '').toLowerCase();
    } catch {
        return '';
    }
}

/** Pop up only for an unread alert, and never for the conversation the person is already reading. */
export function shouldShowPopup(activeThreadId, notification) {
    if (!notification || notification.isRead) return false;
    const sessionId = notificationSessionId(notification);
    return !(sessionId && activeThreadId && sessionId === String(activeThreadId).toLowerCase());
}

/** In-app navigation without a page reload (React Router follows popstate). */
export function openInApp(url) {
    if (typeof window === 'undefined' || !url) return;
    const target = new URL(url, window.location.origin);
    if (target.origin !== window.location.origin) return;
    window.history.pushState({}, '', `${target.pathname}${target.search}${target.hash}`);
    window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
}

/**
 * Brands & Locations rows the bell needs to name an alert's company ("Roger Dodger · The Ride"): business numbers
 * and entities, read through the member's own RLS. A failed read just leaves titles without the company.
 */
export async function fetchAlertBrands({ client = supabaseClient } = {}) {
    try {
        const [numbers, entities] = await Promise.all([
            client.from('org_twilio_numbers').select('phone_number, entity_id'),
            client.from('entities').select('id, parent_entity_id, level_type, name, brand_name, logo'),
        ]);
        return { numbers: numbers?.data || [], entities: entities?.data || [] };
    } catch {
        return { numbers: [], entities: [] };
    }
}
